import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from '../utils/toast';
import { Edit, Download, XCircle } from 'lucide-react';
import axios from 'axios';
import 'react-quill-new/dist/quill.snow.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LeadDetailPage = () => {
  const { leadId } = useParams();
  const navigate = useNavigate();
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchLeadDetail();
  }, [leadId]);

  const fetchLeadDetail = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/crm/leads/${leadId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLead(response.data);
    } catch (error) {
      toast.error('Failed to fetch lead details');
      navigate('/crm');
    } finally {
      setLoading(false);
    }
  };

  const handleDeactivate = async () => {
    if (!window.confirm('Are you sure you want to deactivate this lead?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/crm/leads/${leadId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Lead deactivated successfully');
      navigate('/crm');
    } catch (error) {
      toast.error('Failed to deactivate lead');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F5F6FA] flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#0056B3] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading lead details...</p>
        </div>
      </div>
    );
  }

  if (!lead) return null;

  return (
    <div className="min-h-screen bg-[#F5F6FA]">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <h1 className="text-[22px] font-semibold" style={{ color: '#0056B3' }}>Lead Details</h1>
            <div className="flex space-x-2">
              <Button onClick={() => navigate(`/crm/leads/${leadId}/edit`)} className="bg-[#0056B3] hover:bg-[#0056B3]/90">
                <Edit className="w-4 h-4 mr-2" /> Edit Lead
              </Button>
              <Button onClick={handleDeactivate} variant="outline" className="text-red-600 border-red-600">
                <XCircle className="w-4 h-4 mr-2" /> Deactivate
              </Button>
              <Button onClick={() => navigate('/crm')} variant="outline">Back to Leads</Button>
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-2xl p-8 shadow-xl space-y-8">
          {/* Basic Information */}
          <div>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-[18px] font-semibold" style={{ color: '#0056B3' }}>Institution Information</h2>
              <Badge className="text-lg px-4 py-1">{lead.disposition_status}</Badge>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              <div><p className="text-sm text-gray-600">Lead ID</p><p className="font-medium">{lead.id}</p></div>
              <div><p className="text-sm text-gray-600">Institution Name</p><p className="font-medium">{lead.hospital_name}</p></div>
              <div><p className="text-sm text-gray-600">Institution Type</p><p className="font-medium">{lead.institution_type}</p></div>
              <div><p className="text-sm text-gray-600">City</p><p className="font-medium">{lead.city}, {lead.state}</p></div>
              <div className="md:col-span-2"><p className="text-sm text-gray-600">Address</p><p className="font-medium">{lead.hospital_address}</p></div>
              <div><p className="text-sm text-gray-600">Contact Email</p><p className="font-medium">{lead.contact_email}</p></div>
              <div><p className="text-sm text-gray-600">Contact Phone</p><p className="font-medium">{lead.contact_phone}</p></div>
            </div>
          </div>

          {/* SPOC Details */}
          <div>
            <h2 className="text-[18px] font-semibold mb-4" style={{ color: '#0056B3' }}>SPOC Details</h2>
            {lead.spoc_details?.map((spoc, index) => (
              <div key={index} className="border rounded-lg p-4 mb-4">
                <h3 className="font-medium mb-3">SPOC {index + 1}</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div><p className="text-sm text-gray-600">Name</p><p className="font-medium">{spoc.spoc_name}</p></div>
                  <div><p className="text-sm text-gray-600">Mobile</p><p className="font-medium">{spoc.spoc_mobile}</p></div>
                  <div><p className="text-sm text-gray-600">Email</p><p className="font-medium">{spoc.spoc_email}</p></div>
                  <div><p className="text-sm text-gray-600">Designation</p><p className="font-medium">{spoc.spoc_designation}</p></div>
                  {spoc.remarks && (
                    <div className="md:col-span-2"><p className="text-sm text-gray-600">Remarks</p><p className="font-medium">{spoc.remarks}</p></div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Institution Details */}
          <div>
            <h2 className="text-[18px] font-semibold mb-4" style={{ color: '#0056B3' }}>Institution Details</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div><p className="text-sm text-gray-600">Number of Beds</p><p className="font-medium">{lead.number_of_beds}</p></div>
              <div><p className="text-sm text-gray-600">Number of Staff</p><p className="font-medium">{lead.number_of_staff}</p></div>
              <div><p className="text-sm text-gray-600">Concurrent Staff</p><p className="font-medium">{lead.number_of_concurrent_staff}</p></div>
              <div><p className="text-sm text-gray-600">Staff for Quotation</p><p className="font-medium">{lead.number_of_staff_for_quotation}</p></div>
              {lead.gstin_uin && <div><p className="text-sm text-gray-600">GSTIN/UIN</p><p className="font-medium">{lead.gstin_uin}</p></div>}
              {lead.pan_it && <div><p className="text-sm text-gray-600">PAN/IT</p><p className="font-medium">{lead.pan_it}</p></div>}
              <div><p className="text-sm text-gray-600">Accreditation Status</p><p className="font-medium">{lead.accreditation_status}</p></div>
            </div>
          </div>

          {/* Lead Disposition */}
          <div>
            <h2 className="text-[18px] font-semibold mb-4" style={{ color: '#0056B3' }}>Lead Disposition</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div><p className="text-sm text-gray-600">Lead Source</p><p className="font-medium">{lead.lead_source}</p></div>
              <div><p className="text-sm text-gray-600">Quoted Amount</p><p className="font-medium">₹{lead.quoted_amount?.toLocaleString() || 0}</p></div>
              <div><p className="text-sm text-gray-600">Lead Owner</p><p className="font-medium">{lead.lead_owner_name}</p></div>
              <div><p className="text-sm text-gray-600">Follow-up Count</p><p className="font-medium">{lead.follow_up_count}</p></div>
              {lead.follow_up_action && (
                <div className="md:col-span-2"><p className="text-sm text-gray-600">Follow-up Action</p><p className="font-medium">{lead.follow_up_action}</p></div>
              )}
              {lead.important_notes && (
                <div className="md:col-span-2">
                  <p className="text-sm text-gray-600">Important Notes</p>
                  {/^\s*<(p|ul|ol|br|strong|em|b|i|div|span|h[1-6])[\s>]/i.test(lead.important_notes) ? (
                    <div className="ql-snow">
                      <div
                        className="ql-editor font-medium text-gray-900 !p-0"
                        dangerouslySetInnerHTML={{ __html: lead.important_notes }}
                      />
                    </div>
                  ) : (
                    <p className="font-medium whitespace-pre-wrap">{lead.important_notes}</p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default LeadDetailPage;